import type { StorePlanningTask, StoreProcessTaskType } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useEffect, useState } from 'react'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { TASK_THEME } from './planning/planning-constants'
import { PROCESS_CONTENT_ORDER, PROCESS_DIRECTORY, PROCESS_PLACEHOLDER } from './process-directory'

// Onglet actif : soit un process finalisé, soit l'onglet placeholder « Process 4 — à confirmer ».
type ProcessTabKey = StoreProcessTaskType | 'PLACEHOLDER'

/**
 * Modale documentaire « PROCESS » (Figma 10263:2353 / 2537 / 2679), ouverte au clic sur une tâche
 * du widget PROCESS. Même overlay que `ContactModal` (posée en `fixed`, hors du scale du dashboard).
 *
 * Décrit le mode opératoire (SOP) du process invoqué et permet de naviguer entre les process via
 * une barre d'onglets : 3 process finalisés (Nettoyage / Réception / Livraison) + un onglet
 * placeholder pour les process pas encore définis. Les types de tâche sans SOP (INVENTORY,
 * CLIENT_RELATION) ouvrent la modale sur le placeholder. Contenu 100% statique
 * (`process-directory.ts`) — durée incluse (valeur SOP du registry, alignée maquette) ; le back
 * ne sert qu'à choisir l'onglet initial via `task.type`.
 */
export function ProcessModal({ task, onClose }: { task: StorePlanningTask; onClose: () => void }) {
  // Onglet initial = type de la tâche invoquée s'il a un SOP, sinon le placeholder.
  const [activeTab, setActiveTab] = useState<ProcessTabKey>(
    PROCESS_DIRECTORY[task.type] ? task.type : 'PLACEHOLDER',
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const content = activeTab === 'PLACEHOLDER' ? undefined : PROCESS_DIRECTORY[activeTab]
  const theme = activeTab === 'PLACEHOLDER' ? null : TASK_THEME[activeTab]
  const ActiveIcon = content?.Icon ?? PROCESS_PLACEHOLDER.Icon

  // Onglets rendus : les 3 process finalisés puis l'onglet placeholder.
  const tabs: { key: ProcessTabKey; label: string; Icon: typeof PROCESS_PLACEHOLDER.Icon }[] = [
    ...PROCESS_CONTENT_ORDER.map((type) => ({
      key: type as ProcessTabKey,
      label: PROCESS_DIRECTORY[type]!.tabLabel,
      Icon: PROCESS_DIRECTORY[type]!.Icon,
    })),
    { key: 'PLACEHOLDER', label: PROCESS_PLACEHOLDER.tabLabel, Icon: PROCESS_PLACEHOLDER.Icon },
  ]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(7,44,26,0.25)] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label={`Process ${content?.tabLabel ?? PROCESS_PLACEHOLDER.tabLabel}`}
      onClick={onClose}
    >
      <div
        className="flex flex-col gap-[20px] rounded-[20px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-[12px]">
          <h2 className="font-sans text-[22px] leading-[24px] font-semibold text-[#181D27] uppercase">
            Process
          </h2>
          <ModalCloseButton onClose={onClose} />
        </div>

        {/* Barre d'onglets + boîte icône du process actif */}
        <div className="flex items-center gap-[10px]">
          {tabs.map(({ key, label, Icon: TabIcon }) => {
            const active = key === activeTab
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                aria-pressed={active}
                className={cn(
                  'flex shrink-0 cursor-pointer items-center gap-[7px] rounded-full border px-[19px] py-[11px]',
                  active
                    ? 'border-[#507247] bg-[#507247] text-white'
                    : 'border-[#D7D7D7] bg-white text-[#072C1A]',
                )}
              >
                <TabIcon className="size-[14px]" />
                <span className="font-sans text-[13px] font-semibold whitespace-nowrap">
                  {label}
                </span>
              </button>
            )
          })}
          <div className="flex flex-1 justify-end">
            <div
              className="flex size-[64px] shrink-0 items-center justify-center rounded-[16px] border bg-white"
              style={{ borderColor: theme?.bar ?? '#D7D7D7', color: theme?.text ?? '#8A887F' }}
            >
              <ActiveIcon className="size-[42px]" />
            </div>
          </div>
        </div>

        {/* Corps documentaire (scroll natif) */}
        <div className="flex h-[720px] w-[860px] flex-col gap-[18px] overflow-y-auto kiosk-scroll-y rounded-[16px] border border-[#E3E1D8] bg-white p-[29px]">
          {content ? (
            <>
              <p className="font-sans text-[20px] font-bold text-[#072C1A]">{content.title}</p>

              {/* Bloc méta */}
              <div className="flex gap-[36px] rounded-[12px] border border-[#E3E1D8] bg-[#F9F8F1] px-[19px] py-[15px]">
                {[
                  { label: 'Fréquence', value: content.frequence },
                  { label: 'Durée', value: content.duree },
                  { label: 'Responsable', value: content.responsable },
                ].map((meta) => (
                  <div key={meta.label} className="flex flex-col gap-[2px]">
                    <span className="font-sans text-[11px] font-semibold text-[#8A887F] uppercase">
                      {meta.label}
                    </span>
                    <span className="font-sans text-[14px] font-semibold text-[#072C1A]">
                      {meta.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Intro */}
              <p className="font-sans text-[13px] leading-[20.8px] text-[#3A4438]">
                {content.intro}
              </p>

              {/* Étapes numérotées */}
              {content.steps.map((step, index) => (
                <div key={step.title} className="flex items-start gap-[14px]">
                  <span className="flex size-[28px] shrink-0 items-center justify-center rounded-[14px] bg-[#507247] font-sans text-[13px] font-bold text-white">
                    {index + 1}
                  </span>
                  <div className="flex flex-1 flex-col gap-[7px]">
                    <span className="font-sans text-[15px] font-bold text-[#072C1A]">
                      {step.title}
                    </span>
                    {step.description ? (
                      <span className="font-sans text-[13px] leading-[20.8px] text-[#3A4438]">
                        {step.description}
                      </span>
                    ) : null}
                    {step.bullets ? (
                      <ul className="flex flex-col gap-[5px] pl-[18px]">
                        {step.bullets.map((bullet) => (
                          <li
                            key={bullet}
                            className="relative font-sans text-[13px] leading-[19.5px] text-[#3A4438]"
                          >
                            <span className="absolute -left-[18px]" aria-hidden>
                              •
                            </span>
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ))}

              {/* Points de vigilance */}
              <div className="flex flex-col gap-[8px] rounded-[12px] border border-[#EF8031] bg-[#FBF1E7] px-[19px] py-[17px]">
                <span className="font-sans text-[13px] font-bold text-[#B85B1C] uppercase">
                  Points de vigilance
                </span>
                <ul className="flex flex-col gap-[5px] pl-[18px]">
                  {content.vigilance.map((point) => (
                    <li
                      key={point}
                      className="relative font-sans text-[13px] leading-[19.5px] text-[#7A3B12]"
                    >
                      <span className="absolute -left-[18px]" aria-hidden>
                        •
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            /* Onglet placeholder : process pas encore défini par le client. */
            <div className="flex flex-1 flex-col items-center justify-center gap-[12px] text-center">
              <div className="flex size-[56px] items-center justify-center rounded-[16px] bg-[#F0EDE2] text-[#8A887F]">
                <ActiveIcon className="size-[28px]" />
              </div>
              <p className="font-sans text-[18px] font-bold text-[#072C1A]">Process à confirmer</p>
              <p className="max-w-[420px] font-sans text-[13px] leading-[20px] text-[#3A4438]">
                Ce process n'est pas encore défini. Son mode opératoire sera communiqué
                prochainement.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
